import { Injectable } from '@angular/core';
import { BrandsEnum, BrandThemesEnum, IExtendedLink, IIconLink, ILink, ILinkMedia, ThemesEnum } from '@seb/web-design-system/core';

import { CmsLinkDSComponentInterface, CustomCmsLinkComponent } from '../models';
import { HobStateService } from '../services/hob-state.service';
import { ImageService } from '../services/image.service';
import { ThemesMapper } from './themes.mapper';

@Injectable({
  providedIn: 'root'
})
export class LinkMapper {
  constructor(
    private readonly imageService: ImageService,
    private readonly hobStateService: HobStateService
  ) {}

  static toLink(url: string, linkName: string, external?: boolean | string): ILink {
    if (!!url && !!linkName) {
      const mappedUrl = LinkMapper.decodeUrl(url);
      return { url: mappedUrl, linkName, external: external ? JSON.parse(external as string) : false };
    }
    return undefined;
  }

  static cmsLinkToILink(link: CustomCmsLinkComponent): ILink {
    if (link) {
      return {
        url: LinkMapper.decodeUrl(link.urlLocalized || link.url),
        linkName: link.linkName,
        external: link.target ? JSON.parse(link.target as string) : false
      };
    }
    return undefined;
  }

  static cmsLinkToIExtendedLink(link: CmsLinkDSComponentInterface): IExtendedLink {
    if (link) {
      return {
        theme: ThemesMapper.mapBrandTheme(link.theme),
        ...LinkMapper.cmsLinkToILink(link)
      };
    }
    return undefined;
  }

  static decodeUrl(url: string): string {
    let previousDecodedUrl;
    let decodedUrl = url;
    while (decodedUrl !== previousDecodedUrl) {
      previousDecodedUrl = decodedUrl;
      try {
        decodedUrl = decodeURIComponent(previousDecodedUrl);
      } catch (_e) {
        decodedUrl = unescape(previousDecodedUrl);
      }
    }
    return decodedUrl;
  }

  toIconLink(link: CmsLinkDSComponentInterface): IIconLink {
    if (link) {
      return {
        icon: link.picto
          ? link.picto?.replace(/_/g, '-').toLowerCase()
          : this.imageService.normalizeMedia(link.media),
        buttonIconTitle: link.linkName,
        titleHidden: true,
        link: LinkMapper.cmsLinkToILink(link),
        size: 'logo'
      };
    }
    return undefined;
  }

  toLinkMedia(link: CmsLinkDSComponentInterface): ILinkMedia {
    if (link) {
      return {
        ...LinkMapper.cmsLinkToILink(link),
        media: this.imageService.normalizeMedia(link.media)
      };
    }
    return undefined;
  }

  toCustomButtons(links: CustomCmsLinkComponent[], _themeConfig: ThemesEnum | BrandThemesEnum): IExtendedLink[] {
    const buttons: IExtendedLink[] = [];
    if (links?.length > 0) {
      buttons.push({
        border: false,
        ...LinkMapper.cmsLinkToILink(links[0])
      });
      if (links.length > 1) {
        buttons.push({
          border: true,
          ...LinkMapper.cmsLinkToIExtendedLink(links[1])
        });
      }
    }
    return buttons;
  }

  getBrandUrl(brand: BrandsEnum, url: string): string {
    if (!brand || url.startsWith('http') || url.startsWith('www')) {
      return url;
    }

    url = url.replace(/\/{2,}/g, '/');

    const hobState = this.hobStateService.hobState;

    // if brand is ROWENTA and isn't an active brand
    if (brand === hobState.siteBrand && hobState.liveBrand !== '') {
      return `/${brand.toLowerCase()}${url}`;
    }

    return (hobState.isHob && (brand !== hobState.siteBrand))
      ? `/${brand.toLowerCase()}${url}`
      : url;
  }
}
